import React, { useState, useEffect } from 'react';
import { UserProvider, useUser } from './contexts/UserContext';
import { SliderProvider } from './contexts/SliderContext';
import { ErrorProvider, useError } from './contexts/ErrorContext';
import ErrorModal from './components/ErrorModal/ErrorModal';
import Register from './steps/Register/Register';
import Tutorial from './steps/Tutorial/Tutorial';
import Home from './steps/Home/Home';
import IntroPage from './steps/IntroPage/IntroPage';
import ErrorPage from './steps/ErrorPage/ErrorPage';
import { initGA, trackPageView } from './services/analyticsService';
import LoadingPage from './steps/LoadingPage/LoadingPage';

import styles from './App.module.scss';

import { StepData } from './types/step';

function AppContent() {
  const [currentStep, setCurrentStep] = useState<StepData>('loading');
  const { isAuthenticated, isLoading } = useUser();
  const { error, hideError } = useError();

  // Inizializza Google Analytics
  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    // Rimani in loading finché non ha finito di controllare l'autenticazione
    if (isLoading) {
      setCurrentStep('loading');
      return;
    }
    
    // Una volta completato il caricamento, decide dove andare
    if (isAuthenticated) {
      setCurrentStep('home');
    } else {
      setCurrentStep('intro');
    }
  }, [isAuthenticated, isLoading]);

  // Traccia i cambi di step come pageview
  useEffect(() => {
    const stepTitles: Record<StepData, string> = {
      'loading': 'Loading',
      'intro': 'Intro',
      'register': 'Register',
      'home': 'Home',
      'thankyou': 'Thank You'
    };
    (currentStep !== 'loading' && currentStep !== 'home') && trackPageView(`/${currentStep}`, stepTitles[currentStep] || currentStep);
  }, [currentStep]);

  const changeStep = (step: StepData) => {
    setCurrentStep(step);
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'loading':
        return <LoadingPage />;
      case 'intro':
        return <IntroPage changeStep={changeStep} />;
      // case 'tutorial-first-time':
      //   return <Tutorial changeStep={changeStep} firstTime={true} />;
      // case 'tutorial':
      //   return <Tutorial changeStep={changeStep} />;
      case 'home':
        if (isAuthenticated) {
          return <Home changeStep={changeStep} />;
      } else {
        trackPageView('/register', 'Register');
        return <Register changeStep={changeStep} />;
      }
      default:
        return <LoadingPage />;
    }
  };

  return (
    <div className={styles.app}>
      <main className={styles.main}>
        {renderCurrentStep()}
      </main>
      
      {/* ErrorModal per gestire errori globali */}
      <ErrorModal
        isOpen={!!error}
        title={error?.title || ''}
        message={error?.message || ''}
        onClose={hideError}
        onRetry={error?.onRetry}
        showRetryButton={error?.showRetryButton || false}
      />
    </div>
  );
}

function App() {
  return (
    <ErrorProvider>
      <UserProvider>
        <SliderProvider>
          <AppContent />
        </SliderProvider>
      </UserProvider>
    </ErrorProvider>
  );
}

export default App;
